/**
 * @file        src/components/PhotoStrip.tsx
 * @description Swipeable photo strip for listing cards: up to N slides on native CSS scroll-snap,
 *              a "N / M" counter, desktop-only hover arrows, and a last-slide invitation to the
 *              detail page. No carousel library — the mechanism is the browser's own scroller.
 * @rule        Every slide must exist as a real <img> in the SERVER HTML — nothing may appear only
 *              after a user gesture. Search engines and AI agents read the response, not the
 *              interaction; a gesture-gated slide is an invisible slide.
 * @rule        Every slide renders through ResponsiveImage with the CALLER's `sizes`, never a raw
 *              master and never next/image — that is what keeps a card slot on the 384-wide AVIF
 *              instead of the JPEG master (JCK-IMG-RESP-1, I7/I8).
 * @universal   knowledge/universals/components.md#PhotoStrip
 */
"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { ResponsiveImage } from "@/components/ResponsiveImage";

// === TYPES =================================================================

/** Props for {@link PhotoStrip}. All adaptation happens through these — never fork a card-specific copy. */
export interface PhotoStripProps {
  /** Master URLs, in display order. Empty and single-photo arrays are normal, not edge cases. */
  photos: string[];
  /** Intrinsic widths parallel to `photos`; absent → ResponsiveImage's bare-master fallback. */
  photoWidths?: number[];
  /** Every slide links here — the detail page. */
  href: string;
  /** Base alt; each slide gets `${alt} — фото ${n}`. */
  alt: string;
  /** Per-slot `sizes` attribute, passed through to ResponsiveImage untouched. */
  sizes: string;
  /** How many photos become slides. Default 5. */
  maxSlides?: number;
  /** First slide eager. Default false. */
  priority?: boolean;
  /** Classes for the <img> inside every slide (object-cover etc.). */
  imageClassName?: string;
  /** Aspect box class. Default "aspect-[4/3]". */
  aspectClassName?: string;
  /** Overlay slot rendered above the strip (badges, ribbons) — outside the aria-hidden container. */
  children?: React.ReactNode;
}

// === COMPONENT =============================================================

/**
 * Render up to `maxSlides` photos as a horizontally swipeable, scroll-snapped strip.
 *
 * @important Degraded inputs are the everyday path: 0 photos → an empty aspect box (children still
 * render); 1 photo → a single image with no scroll container, counter or arrows. Only 2+ photos
 * build the strip.
 * @important The strip, its slides and its arrows are hidden from assistive technology — they
 * duplicate a link the card already exposes, and a 24-card page would otherwise gain ~288 tab
 * stops. `alt` and `<img src>` stay in the HTML, so crawlers are unaffected. See ADR N-CARD-STRIP-1.
 */
export function PhotoStrip({
  photos,
  photoWidths,
  href,
  alt,
  sizes,
  maxSlides = 5,
  priority = false,
  imageClassName,
  aspectClassName = "aspect-[4/3]",
  children,
}: PhotoStripProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number | null>(null);
  const [index, setIndex] = useState(0);

  const slides = photos.slice(0, maxSlides);
  const rest = photos.length - slides.length;

  useEffect(() => () => {
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
  }, []);

  const handleScroll = () => {
    if (frameRef.current !== null) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      const el = scrollerRef.current;
      if (!el || el.clientWidth === 0) return;
      const raw = Math.round(el.scrollLeft / el.clientWidth);
      setIndex(Math.min(Math.max(raw, 0), slides.length - 1));
    });
  };

  const scrollToIndex = (event: React.MouseEvent, next: number) => {
    event.preventDefault();
    event.stopPropagation();
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ left: next * el.clientWidth });
  };

  const arrowClass =
    "absolute top-1/2 z-10 hidden h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 text-primary shadow opacity-0 transition-opacity group-hover/strip:opacity-100 disabled:pointer-events-none disabled:opacity-0 md:flex";

  return (
    <div className={cn("group/strip relative overflow-hidden", aspectClassName)}>
      {slides.length === 0 && <div className="absolute inset-0 bg-surface" />}

      {slides.length === 1 && (
        <ResponsiveImage
          masterUrl={slides[0]}
          intrinsicWidth={photoWidths?.[0]}
          alt={`${alt} — фото 1`}
          fill
          sizes={sizes}
          className={imageClassName}
          priority={priority}
        />
      )}

      {slides.length >= 2 && (
        <>
          <div
            ref={scrollerRef}
            onScroll={handleScroll}
            aria-hidden="true"
            className="flex h-full w-full snap-x snap-mandatory overflow-x-auto overflow-y-hidden overscroll-x-contain scroll-smooth motion-reduce:scroll-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {slides.map((photo, i) => (
              <Link
                key={i}
                href={href}
                tabIndex={-1}
                className="relative block h-full w-full flex-none snap-center overflow-hidden"
              >
                <ResponsiveImage
                  masterUrl={photo}
                  intrinsicWidth={photoWidths?.[i]}
                  alt={`${alt} — фото ${i + 1}`}
                  fill
                  sizes={sizes}
                  className={imageClassName}
                  priority={priority && i === 0}
                />
                {rest > 0 && i === slides.length - 1 && (
                  <span className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-b from-black/15 via-black/35 to-black/60 text-center text-white">
                    <span className="font-heading text-sm font-semibold [text-shadow:0_1px_3px_rgba(0,0,0,0.6)]">Ещё {rest} фото</span>
                    <span className="text-xs opacity-80 [text-shadow:0_1px_3px_rgba(0,0,0,0.6)]">смотреть на странице авто</span>
                  </span>
                )}
              </Link>
            ))}
          </div>

          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            disabled={index === 0}
            onClick={(event) => scrollToIndex(event, index - 1)}
            className={cn(arrowClass, "left-2")}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            disabled={index === slides.length - 1}
            onClick={(event) => scrollToIndex(event, index + 1)}
            className={cn(arrowClass, "right-2")}
          >
            <ChevronRight className="h-4 w-4" />
          </button>

          <span className="pointer-events-none absolute right-2 bottom-2 z-10 rounded-full bg-black/60 px-2 py-0.5 text-[11px] tabular-nums text-white" aria-hidden="true">
            {index + 1} / {slides.length}
          </span>
        </>
      )}

      {children}
    </div>
  );
}
